/** The pocket's Sources level (spec §7.2 "Sources ›"): the evidence behind a fact, newest first, page by page; a task's opens it. */
import { getNodeProvenance, type MemoryProvenanceItem } from "@/api/memory";
import {
  Icons,
  PocketRow,
  PocketSection,
  PocketStatement,
  Spinner,
} from "@/components/ui";
import { usePagedQuery } from "@/hooks/use-paged-query";
import { formatRelativeTime, useT, type Translator } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import type en from "@/i18n/messages/frames-memory/en";
import { openTaskDetails } from "@/lib/right-panel-navigation";
function sourceLabel(
  item: MemoryProvenanceItem,
  t: Translator<typeof en>,
): string {
  const source = item.observation;
  if (!source.available)
    return source.deleted ? t("sourceDeleted") : t("sourceRedacted");
  if (source.task_id)
    return t("sourceTask", { id: source.task_id.slice(0, 8) });
  if (source.conversation_id) return t("sourceConversation");
  if (source.operation_id) return t("sourceObserved");
  if (source.verification_id) return t("sourceVerification");
  return source.execution_id
    ? t("sourceTaskOutcome")
    : source.kind.replace(/_/g, " ");
}
export function PocketSourcesLevel({ itemId }: { itemId: string }) {
  const t = useT(framesMemoryMessages);
  const sources = usePagedQuery<MemoryProvenanceItem>(
    itemId,
    (cursor) => getNodeProvenance(itemId, { cursor, limit: 20 }),
    0,
  );
  if (sources.settled && !sources.error && sources.items.length === 0)
    return (
      <PocketStatement
        size="empty"
        title={t("sourcesEmpty")}
        testId="memory-sources-empty"
      />
    );
  return (
    <PocketSection testId="memory-sources">
      {sources.items.map((item) => {
        const taskId = item.observation.available
          ? (item.observation.task_id ?? null)
          : null;
        return (
          <PocketRow
            key={item.evidence_id}
            testId={`memory-source-${item.evidence_id}`}
            leading={
              <Icons.MessageCircle className="h-4 w-4" aria-hidden="true" />
            }
            title={sourceLabel(item, t)}
            secondLine={
              item.excerpt ||
              (item.observation.available
                ? t("noExcerpt")
                : t("sourceUnavailable"))
            }
            trailing={formatRelativeTime(item.observed_at, { style: "narrow" })}
            chevron={taskId !== null}
            onSelect={taskId ? () => void openTaskDetails(taskId) : undefined}
          />
        );
      })}
      {sources.hasMore ? (
        <PocketRow
          testId="memory-sources-more"
          title={t("showMore")}
          onSelect={sources.loadMore}
        />
      ) : null}
      {sources.error ? (
        <PocketRow
          testId="memory-sources-retry"
          title={t("sourcesFailed")}
          onSelect={sources.retry}
        />
      ) : null}
      {sources.loading && sources.items.length === 0 ? (
        <div className="flex justify-center py-3">
          <Spinner className="h-4 w-4 text-muted" />
        </div>
      ) : null}
    </PocketSection>
  );
}
